import { faChevronDown, faChevronUp } from '@fortawesome/pro-regular-svg-icons';
import Button from '@sharedComponents/Button';
import Typography from '@sharedComponents/Typography';
import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { withTranslation } from 'react-i18next';

import * as style from './style.module.css';

const DefaultContainer = ({ children, className }) => <div className={className}>{children}</div>;

const DefaultDescription = ({ children, className, height }) => (
    <div className={`${style.description} ${className || ''}`} style={{ height }}>
        {children}
    </div>
);

const DefaultTextWrapper = ({ children, className, ref, fontSize = 's', fontWeight = 'regular' }) => (
    <Typography className={className} ref={ref} fontSize={fontSize} fontWeight={fontWeight}>
        {children}
    </Typography>
);

const DefaultButton = ({ label, onClick, showFullDescription, className }) => (
    <Button
        label={label}
        className={className}
        onClick={onClick}
        ghost
        noPadding
        icon={showFullDescription ? faChevronUp : faChevronDown}
    />
);

const Spoiler = ({ t, text = '', classNames = {}, components = {}, maxLines = 1 }) => {
    const [showFullDescription, setShowFullDescription] = useState(false);
    const [descHeight, setDescHeight] = useState('auto');
    const [shouldTruncate, setShouldTruncate] = useState(false);
    const measureRef = useRef(null);

    const { Container, Description, TextWrapper, ToggleButton } = useMemo(
        () => ({
            Container: components.Container || DefaultContainer,
            Description: components.Description || DefaultDescription,
            TextWrapper: components.TextWrapper || DefaultTextWrapper,
            ToggleButton: components.Button || DefaultButton,
        }),
        [components],
    );

    const props = {
        willTruncate: shouldTruncate,
        isExpanded: showFullDescription,
    };

    useLayoutEffect(() => {
        if (measureRef.current) {
            const lineHeight = parseInt(window.getComputedStyle(measureRef.current).lineHeight, 10) || 20; // Fallback à 20px
            const fullHeight = measureRef.current.scrollHeight;
            const maxHeight = lineHeight * maxLines;

            const shouldTrunc = fullHeight > maxHeight;
            setShouldTruncate(shouldTrunc);

            if (!showFullDescription) {
                setDescHeight(`${shouldTrunc ? maxHeight : fullHeight}px`);
            } else {
                setDescHeight(`${fullHeight}px`);
            }
        }
    }, [maxLines, showFullDescription, text]);

    return (
        <Container className={classNames.container} {...props}>
            <Description className={classNames.description} height={descHeight} {...props}>
                <TextWrapper
                    className={`${classNames.text} ${!showFullDescription && shouldTruncate ? style.clampText : ''}`}
                    ref={measureRef}
                    style={!showFullDescription && shouldTruncate ? { WebkitLineClamp: maxLines } : {}}
                    {...props}
                >
                    {text}
                </TextWrapper>
            </Description>
            {shouldTruncate && (
                <ToggleButton
                    label={t(showFullDescription ? t('see less') : t('see more'))}
                    className={classNames.cta}
                    onClick={() => setShowFullDescription(!showFullDescription)}
                    icon={showFullDescription ? faChevronUp : faChevronDown}
                    ghost
                    noPadding
                    showFullDescription={showFullDescription}
                    {...props}
                />
            )}
        </Container>
    );
};

export default withTranslation()(Spoiler);
